// Main Wizard class - orchestrates all steps and components
import { Navigation } from './wizard-navigation.js';
import { WizardContent } from './wizard-content.js';
import { SuccessScreen } from './screens/success-screen.js';
import { ErrorScreen } from './screens/error-screen.js';
import { ClipboardUtils } from './utils/clipboard-utils.js';
import { WizardState } from './state/wizard-state.js';
import { ImportOrchestrator } from '../../services/import-orchestrator.js';
import { ImportStatusService } from '../../services/import-status.js';
import { t } from '../../i18n.js';

const NEW_CONTACT_RETRY_MS = 2000;

/**
 * Import Wizard
 * 
 * Orchestrates the wizard UI and coordinates services.
 * Follows Single Responsibility Principle by focusing on UI orchestration.
 */
export class ImportWizard {
  constructor(item, contacts, lookupEmails = []) {
    // Initialize state manager
    this.state = new WizardState(item, contacts, lookupEmails);
    
    // Initialize services
    this.importOrchestrator = new ImportOrchestrator();
    this.importStatusService = new ImportStatusService();
    this.clipboardUtils = new ClipboardUtils();
    
    // UI container
    this.container = null;
    
    // Initialize step components
    this.steps = {
      1: new WizardContent(this)
    };
    
    // Initialize UI components - no progress bar needed for single step
    this.navigation = new Navigation(this);
    this.successScreen = new SuccessScreen(this);
    this.errorScreen = new ErrorScreen(this);
  }

  // Backward compatibility getters/setters for state
  get item() { return this.state.getItem(); }
  get contacts() { return this.state.getContacts(); }
  set contacts(value) { this.state.setContacts(value); }
  get currentStep() { return this.state.getCurrentStep(); }
  get maxSteps() { return this.state.maxSteps; }
  get selectedContacts() { return this.state.getSelectedContacts(); }
  set selectedContacts(value) { this.state.setSelectedContacts(value); }
  get selectedLead() { return this.state.getSelectedLead(); }
  set selectedLead(value) { this.state.setSelectedLead(value); }
  get selectedCampaign() { return this.state.getSelectedCampaign(); }
  set selectedCampaign(value) { this.state.setSelectedCampaign(value); }
  get selectedAttachments() { return this.state.getSelectedAttachments(); }
  set selectedAttachments(value) { this.state.setSelectedAttachments(value); }
  get selectedNotifyUsers() { return this.state.getSelectedNotifyUsers(); }
  set selectedNotifyUsers(value) { this.state.setSelectedNotifyUsers(value); }
  get leadsData() { return this.state.getLeadsData(); }
  get campaignsData() { return this.state.getCampaignsData(); }
  get attachmentsData() { return this.state.getAttachmentsData(); }
  set attachmentsData(value) { this.state.setAttachmentsData(value); }
  get usersData() { return this.state.getUsersData(); }
  get actionTypes() { return this.state.getActionTypes(); }
  set actionTypes(value) { this.state.setActionTypes(value); }
  get actionTypesLoaded() { return this.state.actionTypesLoaded; }
  get selectedActionType() { return this.state.getSelectedActionType(); }
  set selectedActionType(value) { this.state.setSelectedActionType(value); }
  
  // Initialize the wizard
  async init(container) {
    this.container = container;
    await this.render();
  }

  /**
   * Drop focus listeners started after a signature create-contact.
   */
  dispose() {
    this._disposed = true;
    this._stopNewContactWatcher();
  }

  /**
   * After the CRM create-contact window opens, refetch the same emails
   * once the task pane is visible again. One extra try covers a slow save.
   */
  watchForNewContacts() {
    this._stopNewContactWatcher();
    if (!this.state.getLookupEmails().length) return;

    let settled = false;
    let inFlight = false;

    const finish = () => {
      settled = true;
      this._stopNewContactWatcher();
    };

    const refetch = async () => {
      if (settled || inFlight) return false;
      if (this.container && !this.container.isConnected) {
        finish();
        return false;
      }
      inFlight = true;
      try {
        const added = await this._mergeFetchedContacts();
        if (added.length > 0) {
          finish();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to refresh contacts after signature create', error);
        return false;
      } finally {
        inFlight = false;
      }
    };

    const onReturn = () => {
      if (settled || document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('focus', this._onFocus);
      this._onVisibility = null;
      this._onFocus = null;

      refetch().then((found) => {
        if (settled || found) return;
        this._retryTimer = setTimeout(async () => {
          await refetch();
          finish();
        }, NEW_CONTACT_RETRY_MS);
      });
    };

    this._onVisibility = () => {
      if (document.visibilityState === 'visible') onReturn();
    };
    this._onFocus = () => onReturn();
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('focus', this._onFocus);
  }

  _stopNewContactWatcher() {
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    if (this._onFocus) {
      window.removeEventListener('focus', this._onFocus);
      this._onFocus = null;
    }
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  async _mergeFetchedContacts() {
    if (this._disposed) return [];
    const { fetchContactsByEmail } = await import('../../services/api.js');
    const fetched = (await fetchContactsByEmail(this.state.getLookupEmails()))?.contacts || [];
    if (this._disposed || (this.container && !this.container.isConnected)) return [];
    const added = this.state.applyFetchedContacts(fetched);
    if (added.length > 0 && !this._disposed && this.container?.isConnected) {
      const step = this.steps[this.currentStep];
      step?.refreshContactsSection?.();
      step?.refreshContactsSummary?.();
      this.updateNavigation();
    }
    return added;
  }
  
  // Main render method
  async render() {
    if (!this.container) return;
    
    this.container.innerHTML = '';
    
    // Create wizard container
    const wizardContainer = document.createElement('div');
    wizardContainer.className = 'wizard-container consolidated';
    
    // Add step content (no progress bar for single page)
    const stepContent = document.createElement('div');
    stepContent.className = 'wizard-step-content consolidated';
    
    await this.steps[this.currentStep].render(stepContent);
    wizardContainer.appendChild(stepContent);
    
    // Add navigation
    const navigation = this.navigation.render();
    wizardContainer.appendChild(navigation);
    
    this.container.appendChild(wizardContainer);
  }
  
  // Check if user can proceed to next step
  canProceed() {
    return this.steps[this.currentStep].canProceed();
  }
  
  // Navigate to next step
  async nextStep() {
    if (this.state.canProceedToNextStep() && this.canProceed()) {
      this.state.setCurrentStep(this.currentStep + 1);
      await this.render();
    }
  }
  
  // Navigate to previous step
  async previousStep() {
    if (this.state.canGoToPreviousStep()) {
      this.state.setCurrentStep(this.currentStep - 1);
      await this.render();
    }
  }
  
  /**
   * Finish wizard and import to CRM
   * Delegates to ImportOrchestrator for the actual import logic
   */
  async finishWizard() {
    if (this._importInProgress || this._disposed) return;
    this._importInProgress = true;
    this._showLoadingState();
    // Let the task pane paint the loading state before the import work starts.
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (this._disposed) return;

    try {
      // Get sharedWith array
      const sharedWithArray = this.state.getSharedWithArray();
      
      // Perform import using the orchestrator
      const importResult = await this.importOrchestrator.import(
        this.item,
        this.selectedContacts,
        this.state.getSelectedLeadsArray(),
        this.state.getSelectedCampaignsArray(),
        this.selectedAttachments,
        sharedWithArray,
        this.state.getSelectedActionType() || ''
      );
      
      if (importResult.isSuccessful()) {
        // Show success screen
        await this.successScreen.show(
          importResult.activityResponse, 
          importResult.attachmentResults
        );
        
        // Update import status banner
        await this._updateImportStatusBanner();
      } else {
        throw importResult.error || new Error('Import failed');
      }
      
    } catch (error) {
      console.error('Import failed:', error);
      this.errorScreen.show(error);
    }
  }

  /**
   * Show loading state on import button
   */
  _showLoadingState() {
    const importButton = this.container?.querySelector('.wizard-import-btn');
    if (!importButton) return;

    importButton.disabled = true;
    importButton.setAttribute('aria-busy', 'true');
    importButton.classList.add('is-loading');
    importButton.innerHTML = '';

    const spinner = document.createElement('span');
    spinner.className = 'wizard-btn-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.textContent = t('importing');

    importButton.appendChild(spinner);
    importButton.appendChild(label);
  }
  
  /**
   * Update the import status banner after successful import
   * Uses ImportStatusService to avoid code duplication
   */
  async _updateImportStatusBanner() {
    try {
      const isAppointment = this.state.isAppointment();
      
      // Create a fresh status tag using the service
      const newStatusTag = await this.importStatusService.createImportStatusTag(
        this.item,
        isAppointment,
        null // No need for click handler since we're just updating existing banner
      );
      
      if (!newStatusTag) {
        return;
      }
      
      // Find existing banner and replace it
      const existingBanner = document.querySelector('.import-status-tag');
      if (existingBanner) {
        existingBanner.replaceWith(newStatusTag);
      }
    } catch (error) {
      console.error('Failed to update import status banner:', error);
      // Don't fail the import if banner update fails
    }
  }
  
  // Update navigation buttons
  updateNavigation() {
    if (this._importInProgress) return;
    const nextButton = this.container?.querySelector('.wizard-import-btn');
    if (nextButton) {
      nextButton.disabled = !this.canProceed();
    }
  }
  
  // Copy text to clipboard (delegate to utils)
  async copyToClipboard(text, message) {
    return this.clipboardUtils.copyToClipboard(text, message);
  }
  
  // Helper functions
  getAuthInstance() {
    // Dynamic import to avoid circular dependencies
    return import('../../services/auth.js');
  }
  
  truncateText(text, maxLength = 50) {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
}
